<template>
  <div class="flex flex-col w-full h-full overflow-hidden" dir="rtl">
    <!-- Voice setup (first time only) -->
    <div v-if="setupStep !== null" class="flex-1 flex flex-col items-center justify-center px-6 gap-6">
      <!-- Step 1: Owner gender -->
      <template v-if="setupStep === 0">
        <div class="text-center space-y-2">
          <div class="text-2xl">👋</div>
          <h2 class="text-white text-lg font-medium">היי! בואו נתאים את העוזרת</h2>
          <p class="text-white/50 text-sm">איך לפנות אליכם?</p>
        </div>
        <div class="flex gap-3">
          <button
            class="flex-1 px-6 py-4 rounded-2xl bg-white/8 border border-white/10 text-white text-sm hover:bg-white/15 transition-all"
            @click="setupGender = 'male'; setupStep = 1"
          >
            <div class="text-xl mb-1">👨</div>
            בלשון זכר
          </button>
          <button
            class="flex-1 px-6 py-4 rounded-2xl bg-white/8 border border-white/10 text-white text-sm hover:bg-white/15 transition-all"
            @click="setupGender = 'female'; setupStep = 1"
          >
            <div class="text-xl mb-1">👩</div>
            בלשון נקבה
          </button>
        </div>
      </template>

      <!-- Step 2: Preferred voice -->
      <template v-if="setupStep === 1">
        <div class="text-center space-y-2">
          <div class="text-2xl">🎙️</div>
          <h2 class="text-white text-lg font-medium">באיזה קול תעדיפו?</h2>
          <p class="text-white/50 text-sm">קול העוזרת הקולית</p>
        </div>
        <div class="flex gap-3">
          <button
            class="flex-1 px-6 py-4 rounded-2xl bg-white/8 border border-white/10 text-white text-sm hover:bg-white/15 transition-all"
            :class="{ 'ring-2 ring-purple-400 bg-white/15': setupVoice === 'male' }"
            @click="setupVoice = 'male'"
          >
            <div class="text-xl mb-1">🧑</div>
            קול גברי
          </button>
          <button
            class="flex-1 px-6 py-4 rounded-2xl bg-white/8 border border-white/10 text-white text-sm hover:bg-white/15 transition-all"
            :class="{ 'ring-2 ring-purple-400 bg-white/15': setupVoice === 'female' }"
            @click="setupVoice = 'female'"
          >
            <div class="text-xl mb-1">👩</div>
            קול נשי
          </button>
        </div>
        <button
          v-if="setupVoice"
          class="mt-2 px-8 py-3 rounded-full bg-gradient-to-r from-purple-500 to-indigo-600 text-white text-sm font-medium hover:opacity-90 transition-all"
          :disabled="savingPrefs"
          @click="saveSetup"
        >
          {{ savingPrefs ? 'שומר...' : 'בואו נתחיל!' }}
        </button>
      </template>
    </div>

    <!-- Chat area (scrollable) -->
    <div v-else ref="chatArea" class="flex-1 overflow-y-auto px-4 pt-2 pb-4 space-y-3 scroll-smooth">
      <!-- Empty state hint -->
      <div v-if="messages.length === 0 && !streamingUserText && !streamingResponse && agentState === 'listening'" class="flex flex-col items-center justify-center h-full gap-4 px-6 text-center">
        <div class="w-14 h-14 rounded-full bg-white/5 flex items-center justify-center">
          <span class="w-3 h-3 rounded-full bg-green-400 animate-pulse" />
        </div>
        <div class="space-y-2">
          <p class="text-white/80 text-base font-medium">פשוט דברו — אני מקשיבה</p>
          <p class="text-white/40 text-sm leading-relaxed">
            אין צורך ללחוץ על כפתור.<br/>
            זו שיחה טבעית, בדיוק כמו לדבר עם בן אדם.<br/>
            אפשר להפריע לי באמצע, לשאול שאלות ולשנות כיוון — אני פה בשבילכם.
          </p>
        </div>
        <div class="mt-2 flex flex-col gap-1.5 text-white/30 text-xs">
          <span>נסו למשל:</span>
          <span>"מה יש לי ביומן מחר?"</span>
          <span>"תקבעי לרונית תספורת ביום חמישי ב-4"</span>
          <span>"תזיזי את התור של דני לשעה 6"</span>
        </div>
      </div>

      <TransitionGroup name="bubble">
        <template v-for="(msg, i) in messages" :key="'msg-' + i">
          <!-- Customer card bubble -->
          <AiCustomerCard v-if="msg.customerCard" :card="msg.customerCard" />
          <!-- Regular text bubble (user messages hidden — transcription quality too low) -->
          <div
            v-else-if="msg.content && msg.role === 'assistant'"
            class="flex justify-start"
          >
            <div
              class="max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed bg-indigo-500/20 text-white/90 rounded-tr-md"
            >
              {{ msg.content }}
            </div>
          </div>
        </template>
      </TransitionGroup>

      <!-- Streaming user text — hidden (transcription quality too low) -->
      <!-- <div v-if="streamingUserText" class="flex justify-end">
        <div class="max-w-[85%] px-4 py-2.5 rounded-2xl rounded-tl-md bg-white/10 text-white/90 text-sm leading-relaxed">
          {{ streamingUserText }}
          <span class="inline-block w-1 h-4 bg-white/30 animate-blink align-text-bottom mr-0.5" />
        </div>
      </div> -->

      <!-- Streaming assistant response -->
      <div v-if="streamingResponse" class="flex justify-start">
        <div class="max-w-[85%] px-4 py-2.5 rounded-2xl rounded-tr-md bg-indigo-500/20 text-white/90 text-sm leading-relaxed">
          {{ streamingResponse }}
          <span class="inline-block w-1 h-4 bg-white/50 animate-blink align-text-bottom mr-0.5" />
        </div>
      </div>

      <!-- Processing indicator with tool name -->
      <div v-if="agentState === 'processing'" class="flex justify-start">
        <div class="px-4 py-2.5 rounded-2xl rounded-tr-md bg-indigo-500/10 text-white/50 text-sm">
          <span class="inline-flex gap-1.5 items-center">
            <span class="w-1.5 h-1.5 bg-white/40 rounded-full animate-bounce" style="animation-delay:0s" />
            <span class="w-1.5 h-1.5 bg-white/40 rounded-full animate-bounce" style="animation-delay:0.15s" />
            <span class="w-1.5 h-1.5 bg-white/40 rounded-full animate-bounce" style="animation-delay:0.3s" />
            <span v-if="currentToolLabel" class="mr-1 text-white/35 text-xs">{{ currentToolLabel }}</span>
          </span>
        </div>
      </div>
    </div>

    <!-- Bottom bar (always-on mic controls) -->
    <div v-if="setupStep === null" class="shrink-0 px-5 pb-5 pt-3">
      <!-- Error -->
      <div v-if="error" class="mb-3 px-4 py-2 rounded-xl bg-red-500/10 border border-red-500/20">
        <div class="flex items-center justify-between">
          <span class="text-red-300 text-xs">{{ error }}</span>
          <button class="text-red-400/60 text-xs mr-2" @click="store.setError(null)">
            <i class="pi pi-times text-[10px]" />
          </button>
        </div>
      </div>

      <!-- Controls row -->
      <div class="flex items-center justify-center gap-4">
        <!-- Mute toggle -->
        <button
          class="w-10 h-10 rounded-full flex items-center justify-center transition-all"
          :class="isMuted ? 'bg-red-500/20 text-red-300' : 'bg-white/8 text-white/50 hover:text-white/70'"
          @click="toggleMute"
        >
          <svg v-if="!isMuted" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
            <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
            <line x1="12" x2="12" y1="19" y2="22"/>
          </svg>
          <svg v-else xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <line x1="2" x2="22" y1="2" y2="22"/>
            <path d="M18.89 13.23A7.12 7.12 0 0 0 19 12v-2"/>
            <path d="M5 10v2a7 7 0 0 0 12 5"/>
            <path d="M15 9.34V5a3 3 0 0 0-5.68-1.33"/>
            <path d="M9 9v3a3 3 0 0 0 5.12 2.12"/>
            <line x1="12" x2="12" y1="19" y2="22"/>
          </svg>
        </button>

        <!-- Status indicator -->
        <div class="flex items-center gap-2">
          <!-- Live dot -->
          <span
            v-if="agentState === 'listening' && !isMuted"
            class="w-2 h-2 rounded-full bg-green-400 animate-pulse"
          />
          <span
            v-else-if="agentState === 'speaking'"
            class="w-2 h-2 rounded-full bg-blue-400 animate-pulse"
          />
          <span
            v-else-if="agentState === 'processing'"
            class="w-2 h-2 rounded-full bg-amber-400 animate-pulse"
          />
          <span v-else class="w-2 h-2 rounded-full bg-white/20" />

          <span class="text-white/40 text-xs">{{ statusLabel }}</span>
        </div>

        <!-- Stop AI speaking button -->
        <button
          v-if="agentState === 'speaking'"
          class="w-10 h-10 rounded-full bg-white/8 text-white/50 hover:text-white/70 flex items-center justify-center transition-all"
          @click="stopTts"
        >
          <i class="pi pi-stop text-xs" />
        </button>

        <!-- Text input toggle -->
        <button
          v-else-if="!showTextInput"
          class="w-10 h-10 rounded-full bg-white/8 text-white/50 hover:text-white/70 flex items-center justify-center transition-all"
          @click="openTextInput"
        >
          <i class="pi pi-pencil text-xs" />
        </button>
      </div>

      <!-- Text input -->
      <div v-if="showTextInput" class="mt-3 flex items-center gap-2 max-w-[360px] mx-auto">
        <input
          ref="textInputRef"
          v-model="textInput"
          type="text" dir="rtl"
          placeholder="הקלידו כאן..."
          :disabled="agentState === 'processing'"
          class="flex-1 px-4 py-2 bg-white/8 rounded-full text-sm placeholder-white/25 focus:outline-none focus:ring-1 focus:ring-white/15 border border-white/8 disabled:opacity-40"
          @keyup.enter="handleSendText"
        />
        <button
          v-if="textInput.trim()"
          class="w-8 h-8 rounded-full bg-indigo-500/30 flex items-center justify-center"
          :disabled="agentState === 'processing'"
          @click="handleSendText"
        >
          <i class="pi pi-send text-[11px]" style="transform: scaleX(-1)" />
        </button>
        <button v-else class="w-8 h-8 rounded-full bg-white/8 text-white/40 flex items-center justify-center" @click="showTextInput = false">
          <i class="pi pi-times text-[10px]" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, nextTick, onMounted, onUnmounted, ref, watch} from 'vue';
import {useAiAgentStore} from '@app/stores/ai-agent';
import {useAiLive} from '@app/composables/use-ai-live';
import { trackEvent } from '@app/composables/posthog';
import {AiApi} from '@app/api/admin/ai-api';
import AiCustomerCard from './AiCustomerCard.vue';

const store = useAiAgentStore();
const {
  isMuted,
  currentVolume,
  isSpeaking,
  startSession,
  toggleMute,
  stopTts,
  sendTextMessage,
  disconnect,
} = useAiLive();

const toolLabels: Record<string, string> = {
  checkAvailability: 'בודקת זמינות...',
  getMySchedule: 'מביאה את היומן...',
  findCustomer: 'מחפשת לקוח...',
  getCustomerCard: 'מביאה כרטיס לקוח...',
  scheduleAppointment: 'קובעת תור...',
  cancelAppointment: 'מבטלת תור...',
  rescheduleAppointment: 'מזיזה תור...',
  getBusinessInfo: 'מביאה מידע על העסק...',
  findFreeTime: 'מחפשת זמן פנוי...',
  blockTime: 'חוסמת זמן...',
};

const chatArea = ref<HTMLElement | null>(null);
const textInput = ref('');
const textInputRef = ref<HTMLInputElement | null>(null);
const showTextInput = ref(false);

// Voice setup
const setupStep = ref<number | null>(null);
const setupGender = ref<'male' | 'female'>('male');
const setupVoice = ref<'male' | 'female' | null>(null);
const savingPrefs = ref(false);

const loadPreferences = async () => {
  try {
    const { preferences } = await AiApi.getVoicePreferences();
    if (!preferences) {
      setupStep.value = 0;
    } else {
      setupStep.value = null;
    }
  } catch {
    setupStep.value = null;
  }
};

const saveSetup = async () => {
  if (!setupVoice.value) return;
  savingPrefs.value = true;
  try {
    await AiApi.setVoicePreferences({
      ownerGender: setupGender.value,
      preferredVoiceGender: setupVoice.value,
    });
    trackEvent('ai_voice_preferences_saved', {
      ownerGender: setupGender.value,
      preferredVoiceGender: setupVoice.value,
    });
    setupStep.value = null;
    startSession();
  } catch {
    store.setError('לא הצלחתי לשמור את ההגדרות');
  }
  savingPrefs.value = false;
};

const messages = computed(() => store.getMessages);
const agentState = computed(() => store.getAgentState);
const error = computed(() => store.error);
const streamingResponse = computed(() => store.streamingResponse);
const streamingUserText = computed(() => store.streamingUserText);

const currentToolLabel = computed(() => {
  const name = store.currentToolName;
  return name ? toolLabels[name] || 'מעבד...' : null;
});

const statusLabel = computed(() => {
  if (isMuted.value) return 'מושתק';
  switch (agentState.value) {
    case 'listening': return 'מקשיבה';
    case 'processing': return 'מעבדת...';
    case 'speaking': return 'מדברת';
    default: return 'מתחברת...';
  }
});

// Auto-scroll
const scrollToBottom = () => {
  nextTick(() => {
    if (chatArea.value) {
      chatArea.value.scrollTop = chatArea.value.scrollHeight;
    }
  });
};
watch([messages, streamingResponse, streamingUserText, agentState], scrollToBottom, {deep: true});

// ─── Interactions ─────────────────────────────────────────────────

const handleSendText = () => {
  const text = textInput.value.trim();
  if (!text) return;
  trackEvent('ai_agent_text_sent');
  textInput.value = '';
  showTextInput.value = false;
  sendTextMessage(text);
};

const openTextInput = () => {
  showTextInput.value = true;
  nextTick(() => textInputRef.value?.focus());
};

// Connect + open mic on mount (if preferences exist)
onMounted(async () => {
  trackEvent('ai_agent_drawer_opened');
  showTextInput.value = false;
  await loadPreferences();
  if (setupStep.value === null) {
    startSession();
  }
});

// Cleanup on unmount (drawer close)
onUnmounted(() => {
  stopTts();
  disconnect();
  store.closeDrawer();
});
</script>

<style scoped>
/* Cursor blink */
.animate-blink {
  animation: blink 0.8s step-end infinite;
}
@keyframes blink {
  50% { opacity: 0; }
}

/* Chat bubble transitions */
.bubble-enter-active {
  transition: all 0.3s ease-out;
}
.bubble-enter-from {
  opacity: 0;
  transform: translateY(8px);
}
</style>
